import { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "../../../Components/SnackBar";
import { copyText } from "../utils";
import { MOCK_JOIN_LINK } from "../mockData";
import { CARD_BORDER, GREEN, GREEN_TINT, HOVER_BG, SOFT_BG, TEXT_BODY, TEXT_MAIN, TEXT_SUB, g } from "../styles";

const s = {
  card: { border: CARD_BORDER, borderRadius: "10px", backgroundColor: "#fff", overflow: "hidden" },
  head: { display: "flex", alignItems: "flex-start", gap: "4px", padding: "10px 12px" },
  icon: { fontSize: 18, color: TEXT_SUB, marginTop: "1px", marginInlineEnd: "4px" },
  headText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" },
  brief: {
    fontSize: "14px",
    fontWeight: 500,
    color: TEXT_MAIN,
    lineHeight: 1.4,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  metaRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  time: { fontSize: "12px", color: TEXT_SUB },
  iconBtn: { color: "rgba(62, 63, 76, 0.6)", "&:hover": { backgroundColor: HOVER_BG } },
  body: { borderTop: CARD_BORDER, padding: "12px", display: "flex", flexDirection: "column", gap: "8px" },
  sectionLabel: { fontSize: "12px", fontWeight: 500, color: TEXT_SUB },
  variants: { display: "flex", gap: "6px", flexWrap: "wrap" },
  post: {
    fontSize: "14px",
    color: TEXT_BODY,
    lineHeight: 1.55,
    backgroundColor: SOFT_BG,
    border: CARD_BORDER,
    borderRadius: "8px",
    padding: "10px 12px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "6px", marginTop: "4px" },
  action: { ...g.outlineBtn, justifyContent: "flex-start" },
  whatsapp: { color: "rgba(37, 160, 90, 1)", fontSize: 18 },
  facebook: { color: "rgba(24, 119, 242, 1)", fontSize: 18 },
  plainIcon: { fontSize: 18 },
};

const VARIANTS = ["default", "shorter", "warmer"];

// One saved community message: what the manager asked for, and the ready post
// with its versions and share buttons.
const AnnouncementCard = ({ announcement, expanded, onToggle, onDelete, lang, t }) => {
  const snackbar = useSnackbar();
  const [variant, setVariant] = useState("default");
  const post = announcement.posts[variant];

  const time = new Intl.DateTimeFormat(lang, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(
    new Date(announcement.createdAt),
  );

  const actions = [
    {
      key: "whatsapp",
      label: t("share.dialog.whatsapp"),
      icon: <WhatsAppIcon sx={s.whatsapp} />,
      run: () => window.open(`https://wa.me/?text=${encodeURIComponent(post)}`, "_blank", "noopener,noreferrer"),
    },
    {
      key: "facebook",
      label: t("share.dialog.facebook"),
      icon: <FacebookIcon sx={s.facebook} />,
      // Facebook won't take pre-filled text — copy it for pasting into the post.
      run: async () => {
        const copied = await copyText(post);
        snackbar(copied ? t("share.dialog.facebook_text_copied") : t("copy_failed"), copied ? "success" : "error");
        // The real link comes with the saved message; the mock link is for `?preview=1` only.
        const joinLink = announcement.joinLink || MOCK_JOIN_LINK;
        const url = announcement.includeJoinLink
          ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(joinLink)}`
          : "https://www.facebook.com/";
        window.open(url, "_blank", "noopener,noreferrer");
      },
    },
    {
      key: "email",
      label: t("share.dialog.email"),
      icon: <MailOutlineIcon sx={s.plainIcon} />,
      run: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(t("announce.email_subject"))}&body=${encodeURIComponent(post)}`;
      },
    },
    {
      key: "copy",
      label: t("helper.copy"),
      icon: <ContentCopyIcon sx={s.plainIcon} />,
      run: async () => {
        const copied = await copyText(post);
        snackbar(copied ? t("announce.copied") : t("copy_failed"), copied ? "success" : "error");
      },
    },
  ];

  return (
    <Box sx={s.card}>
      <Box sx={s.head}>
        <CampaignOutlinedIcon sx={s.icon} aria-hidden="true" />
        <Box sx={s.headText}>
          <Typography sx={s.brief} dir="auto">{announcement.brief}</Typography>
          <Box sx={s.metaRow}>
            <Typography component="span" sx={s.time}>{time}</Typography>
            {announcement.includeJoinLink && (
              <Box component="span" sx={g.pill(GREEN, GREEN_TINT)}>{t("announce.with_join_link")}</Box>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onToggle} sx={s.iconBtn} aria-expanded={expanded} aria-label={expanded ? t("announce.collapse") : t("announce.expand")}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={onDelete} sx={s.iconBtn} aria-label={t("helper.delete")}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      {expanded && (
        <Box sx={s.body}>
          <Typography sx={s.sectionLabel}>{t("announce.post_title")}</Typography>
          <Box sx={s.variants}>
            {VARIANTS.map((v) => (
              <Button key={v} variant="outlined" size="small" onClick={() => setVariant(v)} sx={g.toggleChip(variant === v)} aria-pressed={variant === v}>
                {t(`helper.variant_${v}`)}
              </Button>
            ))}
          </Box>
          <Typography sx={s.post} dir={announcement.lang === "he" ? "rtl" : "ltr"}>
            {post}
          </Typography>
          <Box sx={s.actions}>
            {actions.map((action) => (
              <Button key={action.key} variant="outlined" size="small" startIcon={action.icon} onClick={action.run} sx={s.action}>
                {action.label}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AnnouncementCard;
